import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form, Space, Tabs, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import {
  AssetTypes,
  useLocationAssignedAssetsByType,
} from 'pos-client/hooks/useLocationAssignedAssets.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { useModalType } from 'pos-client/pages/locations/Modals/useModalType.tsx';
import { RMAssetFormRefinedSchema } from 'pos-client/schemas/SchedulingRefinedSchema.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type RMAssetFormType,
  type RMCreateResourceAssetLinkModel,
  type RMUpdateResourceAssetLinkModel,
  type RouteInstanceModel,
  serviceTypeEnum,
} from 'pos-shared/models/resourceManagementSchema.ts';
import {
  type ResourceWithAssetModel,
  type ServiceTypesRequestModel,
  ServiceTypesRequestSchema,
} from 'pos-shared/models/schedulingSchema.ts';

import RouteDetailsTabContent from './RoutesTabContent.tsx';
import {
  SetupTabContent,
  SetupTabContentSkeleton,
} from './SetupTabContent.tsx';

export type CustomModalPropsData = {
  resource: Partial<ResourceWithAssetModel>;
  startTime: Dayjs;
  endTime: Dayjs;
  serviceLocationId: string;
};
export interface CustomModalProps<T> {
  isVisible: boolean;
  data: T;
  handleOk: () => void;
  handleCancel: () => void;
  serviceTypeFilter: ServiceTypesRequestModel;
}

export const ManageScheduleSettingsModal = ({
  isVisible,
  data,
  handleOk,
  handleCancel,
  serviceTypeFilter,
}: CustomModalProps<CustomModalPropsData>) => {
  const { t } = useTranslation();
  const form = useForm<RMAssetFormType>({
    resolver: zodResolver(RMAssetFormRefinedSchema),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
    watch,
  } = form;

  const { showError, showSuccess } = useNotifications();

  const { Title } = Typography;

  const [currentTab, setCurrentTab] = useState('1');
  const [resourceId, setResourceId] = useState('');
  const trpcContext = trpc.useUtils();
  const { mode, setMode } = useModalType();

  const { assets: technicians, isLoadingAssets: isLoadingTechnicians } =
    useLocationAssignedAssetsByType(
      data.serviceLocationId,
      AssetTypes.Technician
    );

  const { assets: vans, isLoadingAssets: isLoadingAssetsVans } =
    useLocationAssignedAssetsByType(data.serviceLocationId, AssetTypes.Van);

  const { data: responseData, isLoading: isLoadingRoute } =
    trpc.getRouteInstance.useQuery(
      {
        serviceLocationId: data?.serviceLocationId,
        dateOfService: data?.startTime && data?.startTime.format('YYYY-MM-DD'),
        routeInstanceId: data?.resource.id ?? '',
      },
      { enabled: !!data && !!data?.resource.id }
    );

  const routeInstance: RouteInstanceModel | undefined =
    responseData?.results[0];

  const { showModal: showUnsavedChangesModal } = useConfirmModal({
    type: 'delete',
    title: t('common.confirmCancel.unsavedChanges'),
    content: t('common.confirmCancel.unsavedChangesConfirm'),
    confirmText: t('common.buttons.discardChanges'),
    confirmProps: {
      ghost: true,
    },
    onConfirm: handleCancel,
  });

  const onCancelModal = () => {
    if (isDirty) {
      showUnsavedChangesModal();
    } else {
      handleCancel();
    }
  };

  const onSuccessMutation = async () => {
    await trpcContext.getResourcesByLocationId.invalidate();
    await trpcContext.getLinkedAssets.invalidate();

    showSuccess(
      t('common.notification.success.message', {
        action: 'updated',
        entity: 'route settings',
      })
    );
    handleOk();
  };

  const { data: linkedData } = trpc.getLinkedAssets.useQuery({
    serviceType: serviceTypeEnum.enum.Mobile,
    resourceId: data?.resource.id,
    serviceLocationId: data?.serviceLocationId,
  });

  const { mutate: updateScheduleLane, isLoading: isUpdating } =
    trpc.updateRMAsset.useMutation({
      onSuccess: onSuccessMutation,
      onError: async () => {
        showError(
          t('common.errors.error'),
          t('scheduling.lanes.updateLaneError')
        );
      },
    });

  const { mutate: createLink, isLoading: isCreating } =
    trpc.createRMAsset.useMutation({
      onSuccess: onSuccessMutation,
      onError: async () => {
        showError(
          t('common.errors.error'),
          t('scheduling.lanes.createLinkError')
        );
      },
    });

  const handleSaveScheduleLane: SubmitHandler<RMAssetFormType> = (asset) => {
    if (mode === 'create') {
      const newLinkData: RMCreateResourceAssetLinkModel = {
        serviceLocationId: data?.serviceLocationId,
        resourceId: data?.resource.id || '',
        serviceType: serviceTypeFilter,
        dateOfService: data?.startTime?.format('YYYY-MM-DD'),
        technicianAssetId: asset.technician as string,
      };
      if (serviceTypeFilter === ServiceTypesRequestSchema.enum.Mobile) {
        newLinkData.vanAssetId = asset.asset;
      }

      createLink(newLinkData);
    } else {
      const updatedData: RMUpdateResourceAssetLinkModel = {
        id: resourceId,
        technicianAssetId: asset.technician as string,
      };
      if (serviceTypeFilter === ServiceTypesRequestSchema.enum.Mobile) {
        updatedData.vanAssetId = asset.asset;
      }
      updateScheduleLane(updatedData);
    }
  };

  useEffect(() => {
    if (routeInstance) {
      const formValues = watch();

      reset({
        ...formValues,
        ...routeInstance,
        startTimeWindow: {
          start: String(
            dayjs(routeInstance.startTimeWindow.start).format('HH:mm')
          ),
          end: String(dayjs(routeInstance.startTimeWindow.end).format('HH:mm')),
        },
        finishTimeWindow: {
          start: String(
            dayjs(routeInstance.finishTimeWindow.start).format('HH:mm')
          ),
          end: String(
            dayjs(routeInstance.finishTimeWindow.end).format('HH:mm')
          ),
        },
        serviceTimeWindow: {
          start: routeInstance?.serviceTimeWindow?.start
            ? String(
                dayjs(routeInstance.serviceTimeWindow.start).format('HH:mm')
              )
            : '',
          end: routeInstance?.serviceTimeWindow?.end
            ? String(
                dayjs(routeInstance?.serviceTimeWindow?.end).format('HH:mm')
              )
            : '',
        },
      });
    }
  }, [routeInstance]);

  useEffect(() => {
    const isValidLength =
      data.resource.assignedAssets &&
      data.resource.assignedAssets.length &&
      data.resource.assignedAssets.length > 0;

    if (isValidLength) {
      setMode('edit');

      const selectedVan = data.resource.assignedAssets?.find(
        (a) => a.type?.toLowerCase() === 'van'
      )?.id;
      const selectedTechnician = data.resource.assignedAssets?.find(
        (a) => a.type?.toLowerCase() === 'technician'
      )?.id;
      setValue('asset', selectedVan || '');
      setValue('technician', selectedTechnician || '');
    } else {
      setMode('create');
    }
  }, [data.resource]);

  const findResource = () => {
    const resource = linkedData?.results?.find(
      (la) =>
        la.technicianAssetId ===
          data.resource.assignedAssets?.find(
            (a) => a.type?.toLowerCase() === 'technician'
          )?.id &&
        la.vanAssetId ===
          data.resource.assignedAssets?.find(
            (a) => a.type?.toLowerCase() === 'van'
          )?.id
    );
    setResourceId(resource?.id || '');
  };

  useEffect(() => {
    findResource();
  }, [linkedData, data]);

  const isLoading = isLoadingTechnicians || isLoadingAssetsVans;
  const formValues = watch();

  return (
    <PosModal
      header={{
        title:
          serviceTypeFilter === ServiceTypesRequestSchema.enum.Mobile
            ? t('routes.routeSettings')
            : t('scheduling.lanes.laneSettings'),
      }}
      open={isVisible}
      onCancel={onCancelModal}
      destroyOnHidden
      afterClose={reset}
      footer={
        <Space>
          {currentTab === '1' && (
            <>
              <Button onClick={onCancelModal}>
                {t('common.buttons.cancel')}
              </Button>

              <Button
                key="submit"
                type="primary"
                onClick={handleSubmit(handleSaveScheduleLane)}
                disabled={!isDirty}
                loading={isUpdating || isCreating}
              >
                {t('common.buttons.save')}
              </Button>
            </>
          )}
        </Space>
      }
    >
      <Form layout="vertical">
        <Title level={4}>
          {serviceTypeFilter === ServiceTypesRequestSchema.enum.Mobile
            ? t('routes.manageRouteSettings')
            : t('technicians.assignTechnician')}
        </Title>
        <Tabs activeKey={currentTab} onChange={setCurrentTab}>
          <Tabs.TabPane tab={t('routes.setup')} key="1">
            {isLoading ? (
              <SetupTabContentSkeleton />
            ) : (
              <SetupTabContent
                form={form}
                technicians={technicians}
                vans={vans}
                serviceTypeFilter={serviceTypeFilter}
              />
            )}
          </Tabs.TabPane>
          {serviceTypeFilter === ServiceTypesRequestSchema.enum.Mobile && (
            <Tabs.TabPane tab={t('routes.routeDetails')} key="2">
              <RouteDetailsTabContent
                control={control}
                formValues={formValues}
                isLoading={
                  isLoadingRoute || isLoadingTechnicians || isLoadingAssetsVans
                }
              />
            </Tabs.TabPane>
          )}
        </Tabs>
      </Form>
    </PosModal>
  );
};
